/**
 * Price Utilities
 *
 * Functions for extracting and calculating prices from product data
 */

import type { ProductInterface, Money, CurrencyEnum } from '../../../types';

/**
 * Check if product has a special price currently active
 */
export const hasSpecialPrice = (product: ProductInterface): boolean => {
    const { special_price: specialPrice, special_from_date: specialFromDate, special_to_date: specialToDate } = product;
    if (specialPrice && specialFromDate && specialToDate) {
        const now = new Date().getTime();
        const from = new Date(specialFromDate).getTime();
        const to = new Date(specialToDate).getTime();
        return now >= from && now <= to;
    }
    return false;
};

/**
 * Get special price if active
 */
export const getSpecialPrice = (product: ProductInterface): number => {
    const { special_price: specialPrice } = product;
    if (hasSpecialPrice(product)) {
        return specialPrice ?? 0;
    }
    return 0;
};

/**
 * Get final price (the price customer pays)
 */
export const getFinalPrice = (product: ProductInterface): Money => {
    const { price_range: priceRange } = product;
    const minPrice = priceRange?.minimum_price?.final_price;
    const maxPrice = priceRange?.maximum_price?.final_price ?? minPrice;
    const defaultPrice: Money = { value: 0, currency: 'VND' as CurrencyEnum };

    if (!minPrice) {
        return defaultPrice;
    }

    return (maxPrice?.value ?? 0) > (minPrice.value ?? 0) ? (maxPrice ?? defaultPrice) : minPrice;
};

/**
 * Get base/regular price (before discounts)
 */
export const getBasePrice = (product: ProductInterface): Money => {
    const { price_range: priceRange } = product;
    const minPrice = priceRange?.minimum_price?.regular_price;
    const maxPrice = priceRange?.maximum_price?.regular_price ?? minPrice;
    const defaultPrice: Money = { value: 0, currency: 'VND' as CurrencyEnum };

    if (!minPrice) {
        return defaultPrice;
    }

    return (maxPrice?.value ?? 0) > (minPrice.value ?? 0) ? (maxPrice ?? defaultPrice) : minPrice;
};

/**
 * Get minimum price (for configurable products)
 */
export const getMinimalPrice = (product: ProductInterface): Money => {
    const { price_range: priceRange } = product;
    const minPrice = priceRange?.minimum_price?.final_price;
    const maxPrice = priceRange?.maximum_price?.final_price ?? minPrice;
    const defaultPrice: Money = { value: 0, currency: 'VND' as CurrencyEnum };

    if (!minPrice) {
        return defaultPrice;
    }

    return (maxPrice?.value ?? 0) > (minPrice.value ?? 0) ? minPrice : (maxPrice ?? defaultPrice);
};

/**
 * Get maximum price (for configurable products)
 */
export const getMaximumPrice = (product: ProductInterface): Money => {
    const { price_range: priceRange } = product;
    const minPrice = priceRange?.minimum_price?.final_price;
    const maxPrice = priceRange?.maximum_price?.final_price ?? minPrice;
    const defaultPrice: Money = { value: 0, currency: 'VND' as CurrencyEnum };

    if (!minPrice) {
        return defaultPrice;
    }

    return (maxPrice?.value ?? 0) > (minPrice.value ?? 0) ? (maxPrice ?? defaultPrice) : minPrice;
};

/**
 * Get sales percentage between min and max prices
 */
export const getSalesPercentage = (product: ProductInterface): number => {
    const { price_range: priceRange } = product;
    const minPrice = priceRange?.minimum_price?.final_price;
    const maxPrice = priceRange?.maximum_price?.final_price ?? minPrice;

    const minVal = minPrice?.value ?? 0;
    const maxVal = maxPrice?.value ?? 0;

    return maxVal > minVal ? ((maxVal - minVal) / maxVal) * 100 : 0;
};

/**
 * Get discount percentage
 */
export const getDiscountPercent = (product: ProductInterface): number => {
    const { price_range: priceRange } = product;
    const minDiscount = priceRange?.minimum_price?.discount;
    const maxDiscount = priceRange?.maximum_price?.discount ?? minDiscount;

    return Math.max(minDiscount?.percent_off ?? 0, maxDiscount?.percent_off ?? 0);
};
